import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Versión de la política de cookies. Incrementar cuando cambie el alcance
 * (añadir categoría, cambiar finalidad de tracking, etc.). Al detectar
 * mismatch con la decisión guardada, el banner vuelve a aparecer para
 * pedir consent renovado — exigencia RGPD.
 */
export const CURRENT_CONSENT_VERSION = 3

export interface ConsentDecision {
  /** ¿El usuario acepta cookies analíticas (New Relic Browser + Microsoft
   *  Clarity: mapas de calor y grabaciones de sesión)? */
  analytics: boolean
  /** ¿Acepta cookies de publicidad (píxel de Meta: medir qué campañas traen
   *  registros)? Categoría separada de analíticas desde la v3 (AEPD). */
  marketing: boolean
  /** Versión de la política a la que respondió. */
  version: number
  /** Timestamp epoch ms de la decisión, útil para auditoría. */
  decidedAt: number
}

/** Elección por categoría opcional (lo que el user decide en el banner). */
export type ConsentPreferences = Pick<ConsentDecision, 'analytics' | 'marketing'>

interface ConsentState {
  /** Decisión persistida en localStorage. {@code null} hasta que el user decide. */
  decision: ConsentDecision | null
  /**
   * Bandera en memoria — true cuando el user pulsa "Gestionar cookies" en
   * el footer. Fuerza la apertura del banner aunque ya haya decisión
   * guardada. Se limpia al cerrar/guardar.
   */
  bannerOverride: boolean
  /** Acepta todas las categorías opcionales (analíticas + publicidad). */
  acceptAll: () => void
  /** Rechaza todas las opcionales — solo necesarias quedan activas. */
  rejectAll: () => void
  /** Guarda la elección por categoría desde la capa "Configurar". */
  savePreferences: (prefs: ConsentPreferences) => void
  /** Abre el banner desde el footer aunque ya haya consent guardado. */
  reopenBanner: () => void
  /** Cierra el override sin alterar la decisión (cancel desde "Gestionar"). */
  closeBanner: () => void
}

/** Estado resultante de guardar una elección: decisión fechada + banner cerrado. */
function decide(prefs: ConsentPreferences): Pick<ConsentState, 'decision' | 'bannerOverride'> {
  return {
    decision: { ...prefs, version: CURRENT_CONSENT_VERSION, decidedAt: Date.now() },
    bannerOverride: false,
  }
}

/**
 * Store de consent de cookies RGPD.
 *
 * <p>El banner aparece cuando alguna de estas condiciones se cumple:</p>
 * <ul>
 *   <li>No hay decisión guardada ({@code decision === null}).</li>
 *   <li>La versión guardada es menor que {@link CURRENT_CONSENT_VERSION}.</li>
 *   <li>El user pulsó "Gestionar cookies" en el footer ({@code bannerOverride === true}).</li>
 * </ul>
 *
 * <p>Persistimos SOLO {@code decision} — {@code bannerOverride} vive en
 * memoria de la sesión (no queremos que abra el banner solo en cada visita).</p>
 */
export const useConsentStore = create<ConsentState>()(
  persist(
    (set) => ({
      decision: null,
      bannerOverride: false,
      acceptAll: () => set(decide({ analytics: true, marketing: true })),
      rejectAll: () => set(decide({ analytics: false, marketing: false })),
      savePreferences: (prefs) => set(decide(prefs)),
      reopenBanner: () => set({ bannerOverride: true }),
      closeBanner: () => set({ bannerOverride: false }),
    }),
    {
      name: 'matchplay.cookie-consent',
      partialize: (state) => ({ decision: state.decision }),
    },
  ),
)

/**
 * Selector — devuelve true si el banner debe renderizarse. Usar este helper
 * en {@code CookieBanner} en vez de hacer el cálculo inline; mantiene el
 * criterio en un solo sitio si en el futuro evoluciona.
 */
export function useShouldShowBanner(): boolean {
  return useConsentStore((s) => {
    if (s.bannerOverride) return true
    if (!s.decision) return true
    return s.decision.version < CURRENT_CONSENT_VERSION
  })
}

/**
 * Lectura imperativa (no-React) del consent — útil en {@code main.tsx}
 * para decidir si {@code initNewRelic()} arranca antes del primer render.
 *
 * <p>Lee directo de localStorage en vez de usar el store: en el momento
 * que main.tsx llama a esto, React todavía no se ha montado y el store
 * no ha rehidratado. Para evitar la carrera, vamos a la fuente.</p>
 */
export function readConsentFromStorage(): ConsentDecision | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('matchplay.cookie-consent')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: { decision?: ConsentDecision | null } }
    const decision = parsed?.state?.decision
    if (!decision) return null
    if (decision.version < CURRENT_CONSENT_VERSION) return null
    return decision
  } catch {
    return null
  }
}
